"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stats = [{
  label: "§ 05.1 — TAX SAVINGS",
  value: 4250000,
  suffix: "",
  unit: "$ · TOTAL DEDUCTIONS OPTIMIZED",
  note: "Calculated across 450+ individual and corporate tax filings since 2018.",
  color: "terracotta"
}, {
  label: "§ 05.2 — COMPLIANCE",
  value: 99,
  suffix: "%",
  unit: "SUCCESS · AUDIT RESOLUTION RATE",
  note: "Verified through rigorous internal review and regulatory standards.",
  color: "lichen"
}, {
  label: "§ 05.3 — CLIENTS",
  value: 850,
  suffix: "",
  unit: "CLIENTS · FINANCIAL PLANS BUILT",
  note: "Personalized strategies delivered across 22 different industry sectors.",
  color: "terracotta"
}, {
  label: "§ 05.4 — ADVISORY",
  value: 1200,
  suffix: "",
  unit: "HOURS · STRATEGIC CONSULTING",
  note: "Proactive planning sessions helping clients achieve long-term stability.",
  color: "lichen"
}];
const tickerItems = ["TAX-2024", "COMPLIANCE-09", "STRATEGY-M2", "AUDIT-READY", "SAVINGS +$4.2M", "PLAN-117", "ADVISORY +1.2k", "GROWTH +28%", "TAX-031", "COMPLIANCE-14", "SAVINGS +$1.2M"];
function useCountUp(target: number, start: boolean, duration = 1200) {
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!start || startedRef.current) return;
    startedRef.current = true;
    const startTime = performance.now();
    const ease = (t: number) => {
      return 1 - Math.pow(1 - t, 3);
    };
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      setValue(Math.round(target * ease(t)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  return value;
}
function StatBlock({
  stat,
  index,
  triggered
}: {
  stat: (typeof stats)[number];
  index: number;
  triggered: boolean;
}) {
  const animated = useCountUp(stat.value, triggered);
  const colorStyle = stat.color === "terracotta" ? {
    color: "hsl(72 100% 45%)"
  } : {
    color: "hsl(160 84% 39%)"
  };
  return <div data-index={index} className="relative flex flex-col gap-4 px-0 py-8 md:py-0 md:px-8 first:md:pl-0 last:md:pr-0">
      <div className="font-mono uppercase" style={{
      fontSize: "0.6875rem",
      letterSpacing: "0.18em",
      fontWeight: 500,
      color: "hsl(220 10% 40%)"
    }}>
        <span style={{
        color: "hsl(72 100% 45%)"
      }}>
          {stat.label.split("—")[0].trim()}
        </span>
        <span style={{
        color: "hsl(220 10% 40%)"
      }}>
          {" "}— {stat.label.split("—")[1].trim()}
        </span>
      </div>

      <div className="font-default" style={{
      fontSize: "clamp(3.5rem, 7.5vw, 7.5rem)",
      fontWeight: 600,
      letterSpacing: "-0.03em",
      lineHeight: 0.95,
      ...colorStyle
    }}>
        {animated.toLocaleString()}{stat.suffix}
      </div>

      <div className="font-mono uppercase" style={{
      fontSize: "0.75rem",
      letterSpacing: "0.04em",
      fontWeight: 450,
      color: "hsl(220 20% 4%)"
    }}>
        {stat.unit}
      </div>

      <p className="font-default" style={{
      fontSize: "0.9375rem",
      lineHeight: 1.55,
      color: "hsl(220 10% 40%)",
      maxWidth: "28ch"
    }}>
        {stat.note}
      </p>
    </div>;
}
export default function Impact() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    once: true
  });
  return <section data-section-id="2115" id="our-impact-in-numbers" className="relative overflow-hidden bg-background text-foreground">
      {}
      <div className="relative w-full overflow-hidden border-b" style={{
      borderColor: "hsl(220 10% 85%)"
    }}>
        <div className="flex whitespace-nowrap animate-[ticker_60s_linear_infinite] py-3">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => <span data-index={i} key={i} className="font-mono inline-flex items-center" style={{
          fontSize: "0.75rem",
          letterSpacing: "0.04em",
          fontWeight: 450,
          color: "hsl(220 10% 40%)"
        }}>
              <span className="px-6">{item}</span>
              <span className="inline-block h-1 w-1 rounded-lg" style={{
            backgroundColor: "hsl(72 100% 45%)"
          }} aria-hidden="true" />
            </span>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>

      <div ref={sectionRef} className="relative mx-auto w-full max-w-[1440px] px-5 md:px-8 lg:px-14 py-28 md:py-36">
        {}
        <div aria-hidden="true" className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/3 select-none font-default hidden md:block" style={{
        fontSize: "860px",
        lineHeight: 1,
        fontWeight: 700,
        WebkitTextStroke: "1px hsl(220 20% 4%)",
        color: "transparent",
        opacity: 0.03
      }}>
          05
        </div>

        {}
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {}
          <div className="lg:col-span-7 flex flex-col gap-7">
            {}
            <div className="font-mono uppercase" style={{
            fontSize: "0.6875rem",
            letterSpacing: "0.18em",
            fontWeight: 500
          }}>
              <span style={{
              color: "hsl(72 100% 45%)"
            }}>§ 05</span>
              <span style={{
              color: "hsl(220 10% 40%)"
            }}>
                {" "}— FINANCIAL CLARITY / CUMULATIVE 2018–2024
              </span>
            </div>

            {}
            <div className="flex items-center gap-3">
              <div className="h-px flex-1" style={{
              backgroundColor: "hsl(220 10% 85%)"
            }} />
              <div className="flex items-center gap-1.5">
                <span className="inline-block h-1 w-1 rounded-lg" style={{
                backgroundColor: "hsl(72 100% 45%)"
              }} />
                <span className="inline-block h-1 w-1 rounded-lg" style={{
                backgroundColor: "hsl(72 100% 45%)"
              }} />
                <span className="inline-block h-1 w-1 rounded-lg" style={{
                backgroundColor: "hsl(72 100% 45%)"
              }} />
              </div>
            </div>

            {}
            <h2 className="font-default" style={{
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)",
            fontWeight: 600,
            letterSpacing: "-0.02em",
            lineHeight: 1.02,
            color: "hsl(220 20% 4%)"
          }}>
              Results that{" "}
              <span style={{
              fontStyle: "italic"
            }}>define</span> your
              financial future.
            </h2>
          </div>

          {}
          <div className="lg:col-span-5 flex flex-col justify-end gap-6">
            <p className="font-default" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: 1.55,
            color: "hsl(220 10% 40%)"
          }}>
              Years of dedicated tax compliance, strategic planning, and
              financial advisory — measured, audited, and delivered
              to ensure your long-term stability.
            </p>

            <Link to="#" className="group inline-flex items-center gap-2 self-start font-default" style={{
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: "hsl(220 20% 4%)"
          }}>
              <span className="relative inline-block">
                Explore our services
                <span className="absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-0 transition-transform duration-300 ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:scale-x-100" style={{
                backgroundColor: "hsl(220 20% 4%)"
              }} aria-hidden="true" />
              </span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {}
        <div className="mt-20 mb-12 flex items-center gap-3">
          <div className="h-px flex-1" style={{
          backgroundColor: "hsl(220 10% 85%)"
        }} />
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-1 w-1 rounded-lg" style={{
            backgroundColor: "hsl(72 100% 45%)"
          }} />
            <span className="inline-block h-1 w-1 rounded-lg" style={{
            backgroundColor: "hsl(72 100% 45%)"
          }} />
            <span className="inline-block h-1 w-1 rounded-lg" style={{
            backgroundColor: "hsl(72 100% 45%)"
          }} />
          </div>
        </div>

        {}
        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
          {stats.map((stat, i) => <div data-index={i} key={i} className={`relative ${i > 0 ? "lg:border-l" : ""} ${i % 2 === 1 ? "sm:border-l lg:border-l" : ""} ${i >= 2 ? "sm:border-t lg:border-t-0" : ""} border-t sm:border-t-0 first:border-t-0`} style={{
          borderColor: "hsl(220 10% 85%)"
        }}>
              <StatBlock stat={stat} index={i} triggered={isVisible} />
            </div>)}
        </div>
      </div>

      {}
      <div className="relative w-full overflow-hidden border-t" style={{
      borderColor: "hsl(220 10% 85%)"
    }}>
        <div className="flex whitespace-nowrap animate-[ticker_60s_linear_infinite] py-3">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => <span data-index={i} key={i} className="font-mono inline-flex items-center" style={{
          fontSize: "0.75rem",
          letterSpacing: "0.04em",
          fontWeight: 450,
          color: "hsl(220 10% 40%)"
        }}>
              <span className="px-6">{item}</span>
              <span className="inline-block h-1 w-1 rounded-lg" style={{
            backgroundColor: "hsl(72 100% 45%)"
          }} aria-hidden="true" />
            </span>)}
        </div>
      </div>
    </section>;
}